import axios from 'axios';
import {
  REACT_APP_GLOBAL_API_BASE_URL_V2,
  REACT_APP_SIGN_OUT_REDIRECT_URL,
} from 'runtimeEnvVars';

import { TRACKING_VISITOR_KEY } from 'utils/constants';
import { saveIntendedDestination } from 'utils/intendedDestination';
import { getSessionItem } from 'utils/storageUtils';

import {
  DEFAULT_ERROR_MESSAGE,
  NETWORK_ERROR_MESSAGE,
} from '../containers/FlashMessage/constants';
import { SCORECARD_REQUIRED_ERROR_TYPE } from '../containers/ScorecardRequiredModal/types';
import { HIRING_GOAL_REQUIRED_ERROR_TYPE } from './hiringGoalRequiredRejection';
import { NUMBER_OPTED_OUT_CODE } from './numberOptedOutRefusal';
import { SELECT_ALL_COUNT_EXCEEDED_ERROR_TYPE } from './selectAllCountRefusal';

const SIGN_OUT_URL = REACT_APP_SIGN_OUT_REDIRECT_URL;
let cancelToken;

function httpStatusTextOrFallback(errorResponse) {
  const s = (errorResponse.statusText || '').trim();
  if (s) {
    return s;
  }
  if (errorResponse.status === 404) {
    return 'Not found';
  }
  if (errorResponse.status === 403) {
    return 'Forbidden';
  }
  return `Error ${errorResponse.status}`;
}

/**
 * Joins messages into one line. A repeated message shows once. With two or
 * more, each ends in sentence punctuation, so a period is added only where
 * none is; a single message stays as the server wrote it.
 * @param {string[]} parts
 * @returns {string}
 */
function joinMessages(parts) {
  const unique = [...new Set(parts)];
  if (unique.length === 1) {
    return unique[0];
  }
  return unique
    .map(part => (/[.!?]$/.test(part) ? part : `${part}.`))
    .join(' ');
}

/**
 * Maps API `error` / `errors` and similar fields to a single user-visible
 * string. Handles the three shapes Rails endpoints return: a bare string, a
 * message array, and a per-field hash (`{ field => message }` or
 * `{ field => [messages] }` — what `errors.messages` and hand-rolled
 * validators such as ReviewQuestionnaire::Validator produce). Anything with no
 * readable text left in it yields null so the caller can fall back to the next
 * field or the default message.
 * @param {unknown} payload
 * @returns {string | null}
 */
export function apiErrorPayloadToString(payload) {
  if (payload == null) {
    return null;
  }
  if (typeof payload === 'string') {
    const t = payload.trim();
    return t.length ? t : null;
  }
  if (Array.isArray(payload)) {
    if (payload.length === 0) {
      return null;
    }
    const parts = payload
      .map(item => {
        if (typeof item === 'string') {
          return item.trim();
        }
        if (item != null && typeof item === 'object' && 'message' in item) {
          return String(
            /** @type {{ message?: unknown }} */ (item).message,
          ).trim();
        }
        return String(item).trim();
      })
      .filter(part => part.length > 0);
    return parts.length ? joinMessages(parts) : null;
  }
  if (typeof payload === 'object') {
    // Field-error hash. Values recurse, so a hash of strings and a hash of
    // message arrays both resolve; non-text values (numbers, nested nulls)
    // drop out rather than reaching the user as "[object Object]".
    const parts = Object.values(payload)
      .map(value => apiErrorPayloadToString(value))
      .filter(part => part != null);
    return parts.length ? joinMessages(parts) : null;
  }
  return null;
}

/**
 * @param {Record<string, unknown>} data
 * @param {import('axios').AxiosResponse} errorResponse
 * @param {boolean} is403or404
 * @returns {string | null}
 */
function messageFromErrorResponseBody(data, errorResponse, is403or404) {
  const fields = [
    data.error,
    data.errors,
    data.error_message,
    data.error_type,
    data.message,
  ];
  for (let i = 0; i < fields.length; i += 1) {
    const msg = apiErrorPayloadToString(fields[i]);
    if (msg) {
      return msg;
    }
  }
  if (is403or404) {
    return httpStatusTextOrFallback(errorResponse);
  }
  return null;
}

axios.interceptors.response.use(null, error => {
  const isApplicantDetail =
    error?.config?.method === 'get' &&
    (error.config.url ?? '').startsWith(
      `${REACT_APP_GLOBAL_API_BASE_URL_V2}/applicants/`,
    ) &&
    /^[^/?]+(?:\?.*)?$/.test(
      (error.config.url ?? '').slice(
        `${REACT_APP_GLOBAL_API_BASE_URL_V2}/applicants/`.length,
      ),
    );
  const rejectMessage = message =>
    Promise.reject(
      isApplicantDetail ? { message, status: error.response?.status } : message,
    );
  if (error && error.response) {
    if (error.response.status === 401) {
      saveIntendedDestination();
      const visitorTrack = getSessionItem(TRACKING_VISITOR_KEY);
      window.location.replace(`${SIGN_OUT_URL}?app=${visitorTrack}`);
    }
    const { data, status } = error.response;
    // Reordering needs the conflict status to roll back and reread the server order.
    if (
      status === 409 &&
      error.config?.method === 'patch' &&
      /\/api_self_serve\/v2\/hiring_goal_positions\/[^/?]+$/.test(
        error.config.url ?? '',
      ) &&
      data?.code === 'stale_order'
    ) {
      return Promise.reject({ status, code: data.code });
    }
    if (status >= 500) {
      return rejectMessage(DEFAULT_ERROR_MESSAGE);
    }
    const is403or404 = status === 403 || status === 404;
    // The scorecard-requirement refusal is the one error the UI renders as a
    // modal, not a toast, so it keeps its structure instead of flattening to
    // a string. Shape: containers/ScorecardRequiredModal/types.ts.
    if (
      data != null &&
      typeof data === 'object' &&
      data.error_type === SCORECARD_REQUIRED_ERROR_TYPE
    ) {
      return Promise.reject({
        errorType: data.error_type,
        message: apiErrorPayloadToString(data.error) ?? DEFAULT_ERROR_MESSAGE,
        scorecardRequirement: data.scorecard_requirement ?? null,
      });
    }
    // The hiring goal refusal keeps its structure too, so the move hooks can
    // tell a policy answer from a failure. Shape: utils/hiringGoalRequiredRejection.ts.
    if (
      data != null &&
      typeof data === 'object' &&
      data.error_type === HIRING_GOAL_REQUIRED_ERROR_TYPE
    ) {
      return Promise.reject({
        errorType: data.error_type,
        message: apiErrorPayloadToString(data.error) ?? DEFAULT_ERROR_MESSAGE,
        hiringGoalRequirement: data.hiring_goal_requirement ?? null,
      });
    }
    // A refused select-all keeps its type, so the bulk hooks show Hire's
    // message instead of the generic error. Shape: utils/selectAllCountRefusal.ts.
    if (
      data != null &&
      typeof data === 'object' &&
      data.error_type === SELECT_ALL_COUNT_EXCEEDED_ERROR_TYPE
    ) {
      return Promise.reject({
        errorType: data.error_type,
        message: apiErrorPayloadToString(data.error) ?? DEFAULT_ERROR_MESSAGE,
      });
    }
    // A send refused for an opted-out number keeps its code, so Resend shows
    // Hire's reason instead of the generic error. Shape: utils/numberOptedOutRefusal.ts.
    if (
      data != null &&
      typeof data === 'object' &&
      data.code === NUMBER_OPTED_OUT_CODE
    ) {
      return Promise.reject({
        code: data.code,
        message: apiErrorPayloadToString(data.message) ?? DEFAULT_ERROR_MESSAGE,
      });
    }
    if (data != null && typeof data === 'object' && !Array.isArray(data)) {
      const message =
        messageFromErrorResponseBody(data, error.response, is403or404) ??
        DEFAULT_ERROR_MESSAGE;
      return rejectMessage(message);
    }
    if (typeof data === 'string' && is403or404 && data.trim().length) {
      const withoutTags = data.replace(/<[^>]+>/g, ' ').trim();
      if (withoutTags) {
        return rejectMessage(
          withoutTags.length > 240
            ? `${withoutTags.slice(0, 240)}…`
            : withoutTags,
        );
      }
    }
    if (is403or404) {
      return rejectMessage(httpStatusTextOrFallback(error.response));
    }
    return rejectMessage(DEFAULT_ERROR_MESSAGE);
  }
  if (axios.isCancel(error)) {
    return Promise.reject(error);
  }
  return rejectMessage(NETWORK_ERROR_MESSAGE);
});

const config = {
  withCredentials: true,
};

export function apiGet(path, params, requestConfig) {
  return axios.get(path, { ...config, params, ...requestConfig });
}
export function apiPost(path, data, requestConfig) {
  return axios.post(path, data, { ...config, ...requestConfig });
}

export function apiPatch(path, data) {
  return axios.patch(path, data, config);
}

export function apiPut(path, data) {
  return axios.put(path, data, config);
}

export function apiDelete(path, data) {
  return axios.delete(path, { ...config, ...data });
}

export function apiGetWithCancelRequest(path, params) {
  if (cancelToken) {
    cancelToken.cancel('request canceled');
  }
  cancelToken = axios.CancelToken.source();
  return axios.get(path, {
    ...config,
    params,
    ...{ cancelToken: cancelToken.token },
  });
}

/** Cancels the in-flight `apiGetWithCancelRequest` (e.g. messenger messages) without starting a new request. */
export function cancelPendingApiGetWithCancelRequest() {
  if (cancelToken) {
    cancelToken.cancel('request canceled');
  }
}
